import {
  createBigDecimal,
  divide,
  minus,
  plus,
  times,
} from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type {
  TradeSubCategory,
  TransactionTransfer,
} from '../../types/transaction'
import { createAssetKey } from '../../utils'
import {
  getTotalPl,
  splitTradePlByNftHoldingPeriod,
} from '../utils/transactionTransferPl'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'
import { addBalanceToAssetBorrowsAndAssetBalances } from './util/addBalanceToAssetBorrowsAndAssetBalances'
import { getCostByAssetBalancesAndBorrows } from './util/getCostByAssetBalancesAndBorrows'
import { updateCostBy5PercentRule } from './util/updateCostBy5PercentRule'

type CreateTradeTransactionParameters = CreateCategoryTransactionParameters & {
  readonly subCategory?: TradeSubCategory
  readonly inTransfers: readonly Transfer[]
  readonly outTransfers: readonly Transfer[]
  readonly costBasis: CostBasis
  readonly use5PercentRule?: boolean
}

type CreateTradeTransactionReturnTypes = CreateCategoryTransactionReturnTypes

export const createTradeTransaction = ({
  actionId,
  timestamp,
  subCategory,
  inTransfers,
  outTransfers,
  assetBalances,
  assetBorrows,
  usedBalances,
  nftAcquiredTimestamps,
  costBasis,
  use5PercentRule = false,
}: CreateTradeTransactionParameters): CreateTradeTransactionReturnTypes => {
  // 価格を決定する
  const inTransactionTransfers: TransactionTransfer[] = []
  const outTransactionTransfers: TransactionTransfer[] = []
  const transferCounts: {
    all: number
    in: number
    out: number
    unknownPrice: number
    inUnknownPrice: number
    outUnknownPrice: number
    inNftUnknownPrice: number
    outNftUnknownPrice: number
    inFiat: number
    outFiat: number
  } = {
    all: 0,
    in: 0,
    out: 0,
    unknownPrice: 0,
    inUnknownPrice: 0,
    outUnknownPrice: 0,
    inNftUnknownPrice: 0,
    outNftUnknownPrice: 0,
    inFiat: 0,
    outFiat: 0,
  }
  for (const transfer of inTransfers) {
    transferCounts.all += 1
    transferCounts.in += 1
    if (transfer.price === undefined) {
      transferCounts.unknownPrice += 1
      transferCounts.inUnknownPrice += 1
      if (transfer.asset.type === 'Nft') {
        transferCounts.inNftUnknownPrice += 1
      }
    }
    if (transfer.asset.type === 'FiatCurrency') {
      transferCounts.inFiat += 1
    }
  }
  for (const transfer of outTransfers) {
    transferCounts.all += 1
    transferCounts.out += 1
    if (transfer.price === undefined) {
      transferCounts.unknownPrice += 1
      transferCounts.outUnknownPrice += 1
      if (transfer.asset.type === 'Nft') {
        transferCounts.outNftUnknownPrice += 1
      }
    }
    if (transfer.asset.type === 'FiatCurrency') {
      transferCounts.outFiat += 1
    }
  }
  // in のみに unknown price transfer があるかつ全て NFT の場合
  if (
    transferCounts.inUnknownPrice > 0 &&
    transferCounts.outUnknownPrice === 0 &&
    transferCounts.inUnknownPrice === transferCounts.inNftUnknownPrice
  ) {
    const inTotalValue = inTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const outTotalValue = outTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const diff = minus(outTotalValue, inTotalValue)
    const unknwonAmount = inTransfers.reduce(
      (acc, cur) =>
        plus(acc, cur.price === undefined ? cur.amount : createBigDecimal(0n)),
      createBigDecimal(0n),
    )
    const unknownPrice =
      diff.value <= 0n || unknwonAmount.value === 0n
        ? createBigDecimal(0n)
        : divide(times(diff, createBigDecimal(1n), 18), unknwonAmount, 18)
    for (const transfer of inTransfers) {
      const zeroCompleted = transfer.price === undefined && diff.value < 0n
      const price = transfer.price ?? unknownPrice
      inTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
    for (const transfer of outTransfers) {
      const price = transfer.price ?? createBigDecimal(0n)
      outTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
  }
  // out のみに unknown price transfer があるかつ全て NFT の場合
  else if (
    transferCounts.inUnknownPrice === 0 &&
    transferCounts.outUnknownPrice > 0 &&
    transferCounts.outUnknownPrice === transferCounts.outNftUnknownPrice
  ) {
    const inTotalValue = inTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const outTotalValue = outTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const diff = minus(inTotalValue, outTotalValue)
    const unknwonAmount = outTransfers.reduce(
      (acc, cur) =>
        plus(acc, cur.price === undefined ? cur.amount : createBigDecimal(0n)),
      createBigDecimal(0n),
    )
    const unknownPrice =
      diff.value <= 0n || unknwonAmount.value === 0n
        ? createBigDecimal(0n)
        : divide(times(diff, createBigDecimal(1n), 18), unknwonAmount, 18)
    for (const transfer of inTransfers) {
      const price = transfer.price ?? createBigDecimal(0n)
      inTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
    for (const transfer of outTransfers) {
      const zeroCompleted = transfer.price === undefined && diff.value < 0n
      const price = transfer.price ?? unknownPrice
      outTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
  }
  // unknown price transfer count > 1 の場合はゼロ補完
  else if (transferCounts.unknownPrice > 1) {
    for (const transfer of inTransfers) {
      const price = transfer.price ?? createBigDecimal(0n)
      inTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
    for (const transfer of outTransfers) {
      const price = transfer.price ?? createBigDecimal(0n)
      outTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
  }
  // unknown price transfer count = 1 の場合は、in/out の合計価値が同一になるように補完
  else if (transferCounts.inUnknownPrice === 1) {
    const inTotalValue = inTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const outTotalValue = outTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const diff = minus(outTotalValue, inTotalValue)
    for (const transfer of inTransfers) {
      const zeroCompleted = transfer.price === undefined && diff.value < 0n
      const price =
        transfer.price ??
        (diff.value <= 0n || transfer.amount.value === 0n
          ? createBigDecimal(0n)
          : divide(times(diff, createBigDecimal(1n), 18), transfer.amount, 18))
      inTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
    for (const transfer of outTransfers) {
      const price = transfer.price ?? createBigDecimal(0n)
      outTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl: createBigDecimal(0n),
        airdropPl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
  } else if (transferCounts.outUnknownPrice === 1) {
    const inTotalValue = inTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const outTotalValue = outTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const diff = minus(inTotalValue, outTotalValue)
    for (const transfer of inTransfers) {
      const price = transfer.price ?? createBigDecimal(0n)
      inTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
    for (const transfer of outTransfers) {
      const zeroCompleted = transfer.price === undefined && diff.value < 0n
      const price =
        transfer.price ??
        (diff.value <= 0n || transfer.amount.value === 0n
          ? createBigDecimal(0n)
          : divide(times(diff, createBigDecimal(1n), 18), transfer.amount, 18))
      outTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
  }
  // out fiat transfer count = out transfer count の場合は、 out の合計価値固定して、 in の合計価値が同一になるように比率で再計算
  else if (transferCounts.outFiat === transferCounts.out) {
    const inTotalValue = inTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const outTotalValue = outTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    for (const transfer of inTransfers) {
      const price =
        inTotalValue.value === 0n
          ? createBigDecimal(0n)
          : divide(
              times(outTotalValue, transfer.price ?? createBigDecimal(0n), 18),
              inTotalValue,
              18,
            )
      inTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: false,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
    for (const transfer of outTransfers) {
      outTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price: transfer.price ?? createBigDecimal(0n),
        zeroCompleted: transfer.price === undefined,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
  }
  // それ以外の場合は in の合計価値固定して、 out の合計価値が同一になるように比率で再計算
  else {
    const inTotalValue = inTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    const outTotalValue = outTransfers.reduce(
      (acc, cur) =>
        plus(acc, times(cur.amount, cur.price ?? createBigDecimal(0n), 6)),
      createBigDecimal(0n),
    )
    for (const transfer of inTransfers) {
      inTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price: transfer.price ?? createBigDecimal(0n),
        zeroCompleted: transfer.price === undefined,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
    for (const transfer of outTransfers) {
      const price =
        outTotalValue.value === 0n
          ? createBigDecimal(0n)
          : divide(
              times(inTotalValue, transfer.price ?? createBigDecimal(0n), 18),
              outTotalValue,
              18,
            )
      outTransactionTransfers.push({
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: false,
        cost: createBigDecimal(0n),
        balance: createBigDecimal(0n),
        borrowing: createBigDecimal(0n),
        feePl: createBigDecimal(0n),
        incomePl: createBigDecimal(0n),
        tradePl: createBigDecimal(0n),
        totalPl: createBigDecimal(0n),
      })
    }
  }

  // コスト＆損益を決定する
  for (const transfer of outTransactionTransfers) {
    const key = createAssetKey(transfer.asset)
    const value = times(transfer.amount, transfer.price, 6)

    // Get cost by asset balance & asset borrow
    const {
      cost,
      assetBalances: keyAssetBalances,
      assetBorrows: keyAssetBorrows,
      usedBalance,
    } = transfer.asset.type === 'FiatCurrency'
      ? {
          cost: value,
          assetBalances: [],
          assetBorrows: [],
          usedBalance: {
            amount: createBigDecimal(0n),
            value: createBigDecimal(0n),
          },
        }
      : getCostByAssetBalancesAndBorrows({
          amount: transfer.amount,
          assetBalances: assetBalances.get(key) ?? [],
          assetBorrows: assetBorrows.get(key) ?? [],
          usedBalance: usedBalances.get(key) ?? {
            amount: createBigDecimal(0n),
            value: createBigDecimal(0n),
          },
          costBasis,
        })
    if (keyAssetBalances.length > 0) {
      assetBalances.set(key, keyAssetBalances)
    } else {
      assetBalances.delete(key)
    }
    if (keyAssetBorrows.length > 0) {
      assetBorrows.set(key, keyAssetBorrows)
    } else {
      assetBorrows.delete(key)
    }
    if (usedBalance.amount.value !== 0n) {
      usedBalances.set(key, usedBalance)
    } else {
      usedBalances.delete(key)
    }

    const costWith5PercentRule = use5PercentRule
      ? updateCostBy5PercentRule(cost, value)
      : cost
    const balance = (assetBalances.get(key) ?? []).reduce(
      (acc, assetBalance) => plus(acc, assetBalance.amount),
      createBigDecimal(0n),
    )
    const borrowing = (assetBorrows.get(key) ?? []).reduce(
      (acc, assetBorrow) => plus(acc, assetBorrow.amount),
      createBigDecimal(0n),
    )

    transfer.cost = cost
    transfer.balance = balance
    transfer.borrowing = borrowing
    const classifiedTradePl = splitTradePlByNftHoldingPeriod({
      tradePl: minus(value, costWith5PercentRule),
      transfer,
      timestamp,
      nftAcquiredTimestamps,
    })
    transfer.tradePl = classifiedTradePl.tradePl
    transfer.separateTradePl = createBigDecimal(0n)
    transfer.nftTradePl = classifiedTradePl.nftTradePl
    transfer.nftCapitalPl = classifiedTradePl.nftCapitalPl
    transfer.separateRealizedPl = createBigDecimal(0n)
    transfer.realizedPl = createBigDecimal(0n)
    transfer.airdropPl = createBigDecimal(0n)
    transfer.totalPl = getTotalPl({ transfer })
  }
  for (const transfer of inTransactionTransfers) {
    const key = createAssetKey(transfer.asset)
    const value = times(transfer.amount, transfer.price, 6)

    // Add balance
    const { assetBalances: keyAssetBalances, assetBorrows: keyAssetBorrows } =
      transfer.asset.type === 'FiatCurrency'
        ? {
            assetBalances: [],
            assetBorrows: [],
          }
        : addBalanceToAssetBorrowsAndAssetBalances({
            amount: transfer.amount,
            value,
            assetBalances: assetBalances.get(key) ?? [],
            assetBorrows: assetBorrows.get(key) ?? [],
            costBasis,
          })
    if (keyAssetBalances.length > 0) {
      assetBalances.set(key, keyAssetBalances)
    } else {
      assetBalances.delete(key)
    }
    if (keyAssetBorrows.length > 0) {
      assetBorrows.set(key, keyAssetBorrows)
    } else {
      assetBorrows.delete(key)
    }

    const balance = (assetBalances.get(key) ?? []).reduce(
      (acc, assetBalance) => plus(acc, assetBalance.amount),
      createBigDecimal(0n),
    )
    const borrowing = (assetBorrows.get(key) ?? []).reduce(
      (acc, assetBorrow) => plus(acc, assetBorrow.amount),
      createBigDecimal(0n),
    )

    transfer.cost = value
    transfer.balance = balance
    transfer.borrowing = borrowing
    transfer.separateTradePl = createBigDecimal(0n)
    transfer.nftTradePl = createBigDecimal(0n)
    transfer.nftCapitalPl = createBigDecimal(0n)
    transfer.separateRealizedPl = createBigDecimal(0n)
    transfer.realizedPl = createBigDecimal(0n)
    transfer.airdropPl = createBigDecimal(0n)
  }

  const transaction = {
    actionId,
    timestamp,
    order: 0,
    category: 'trade',
    subCategory,
    ins: inTransactionTransfers,
    outs: outTransactionTransfers,
  } satisfies CreateTradeTransactionReturnTypes['transaction']

  return {
    transaction,
    assetBalances,
    assetBorrows,
    usedBalances,
    nftAcquiredTimestamps,
  }
}
